<template>
  <div class="flex-1 min-h-0 flex flex-col bg-[#FCFCFD] overflow-hidden">
    <div
      class="flex-1 min-h-0 flex flex-col overflow-y-auto w-full px-4 tablet-h:px-5 pt-[calc(env(safe-area-inset-top)+62px)] pb-[calc(env(safe-area-inset-bottom)+100px)] tablet-h:pt-10 tablet-h:pb-10"
    >
      <div class="w-full max-w-[1342px] mx-auto">
        <LinkBreadcrumb
          v-if="!loading"
          class="mb-5"
          :items="breadcrumbItems"
          @select="handleBreadcrumbSelect"
        />

        <!-- Header skeleton -->
        <div
          v-if="loading"
          class="w-full min-h-[120px] px-[15px] tablet-h:px-[30px] py-5 border border-[#cccccc]/50 rounded-xl flex tablet-h:items-center gap-x-5 mb-[18px]"
        >
          <Skeleton class="min-w-[80px] h-[80px] rounded-md" />
          <div class="flex-1">
            <Skeleton class="w-[200px] h-[26px] mb-3" />
            <div class="flex gap-x-10">
              <div>
                <Skeleton class="w-[120px] h-[18px] mb-1.5" />
                <Skeleton class="w-[160px] h-[18px]" />
              </div>
              <div>
                <Skeleton class="w-[100px] h-[18px] mb-1.5" />
                <Skeleton class="w-[140px] h-[18px]" />
              </div>
            </div>
          </div>
        </div>

        <!-- Header -->
        <div
          v-else
          class="w-full min-h-[120px] px-[15px] tablet-h:px-[30px] py-5 border border-[#cccccc]/50 rounded-xl flex tablet-h:items-center gap-x-5 mb-[18px]"
        >
          <div
            class="min-w-[80px] h-[80px] border border-[#cccccc]/50 rounded-md bg-[#FCFCFD] flex items-center justify-center"
          >
            <div class="size-[46px]">
              <Thumbnail
                :type="fileTypeToThumbnail(file?.type)"
                :src="previewSrc"
              />
            </div>
          </div>

          <div>
            <div class="flex mb-3 gap-x-5">
              <div class="font-medium text-[17px] leading-[26px] text-gray-800">
                {{ file?.name }}
              </div>

              <RouterLink
                :to="fileRoute"
                class="w-fit! tablet-h:hidden self-start ml-auto"
              >
                <Button variant="stroke" :size="isMobile ? 'lg' : 'md'">
                  View
                </Button>
              </RouterLink>
            </div>

            <div
              class="flex flex-col tablet-h:flex-row tablet-h:items-center gap-x-10 gap-y-4"
            >
              <div>
                <div
                  class="font-medium text-[13px] leading-[18px] text-gray-800 flex items-center mb-1.5"
                >
                  <DacrMarkIcon class="size-[16px] text-gray-800 mr-1" />
                  <div>Dacr Timestamp</div>
                </div>

                <div
                  class="text-[14px] leading-[18px] text-gray-800 flex items-center"
                >
                  {{
                    formatTimestamp(file?.copyright.registrations[0]?.timestamp)
                  }}
                </div>
              </div>

              <div>
                <div
                  class="font-medium text-[13px] leading-[18px] text-gray-800 flex items-center mb-1.5"
                >
                  <div>Dacr Token ID</div>
                </div>

                <div
                  class="text-[14px] leading-[18px] text-gray-800 flex items-center break-all"
                >
                  {{ file?.copyright.id }}
                </div>
              </div>
            </div>
          </div>

          <RouterLink
            :to="fileRoute"
            class="hidden tablet-h:block self-start ml-auto"
          >
            <Button variant="stroke" :size="isMobile ? 'lg' : 'md'">
              View registration
            </Button>
          </RouterLink>
        </div>

        <div
          class="w-full p-5 tablet-h:px-[30px] bg-white rounded-xl border-2 border-transparent border--glow relative mb-4"
        >
          <ProtectionBadge />

          <div
            class="border--glow border--glow-frame pointer-events-none absolute -inset-0.5 z-[4] rounded-xl border-2 border-transparent"
          ></div>

          <div class="flex items-center pb-5 mb-4 border-b border-gray-200">
            <TextIcon class="size-[18px] mr-[9px] shrink-0 text-gray-800" />
            <div
              class="w-full text-[16px] leading-[22px] font-medium text-gray-800"
            >
              Transcript
            </div>
          </div>

          <div class="text-[14px] leading-[18px] text-gray-800">
            <div class="mb-[22px]">
              <div class="flex items-center gap-1">
                Analyzed with
                <DacrAIIcon class="h-[13px] w-auto" aria-label="Dacr AI" />
              </div>
            </div>

            <!-- Content skeleton -->
            <template v-if="transcriptLoading">
              <div v-for="n in 5" :key="n" class="flex mb-1">
                <div class="min-w-[90px] mr-3">
                  <Skeleton class="w-[50px] h-[20px]" />
                </div>
                <Skeleton
                  class="h-[20px]"
                  :style="{ width: `${20 + ((n * 8) % 25)}%` }"
                />
              </div>
            </template>

            <!-- Content -->
            <template v-else>
              <div
                v-for="entry in transcript"
                :key="entry.timestamp"
                class="leading-[20px] flex"
              >
                <div class="min-w-[90px] text-gray-500 tabular-nums">
                  {{ entry.timestamp }}
                </div>
                <div>{{ entry.text }}</div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useCopyrightFile } from '@/features/copyright/composables/useCopyrightFile'
import { formatTimestamp } from '@/shared/utils/datetime'
import { applyAuthorizationToken } from '@/shared/utils/url'
import { fileTypeToThumbnail } from '@/shared/utils/thumbnail'
import {
  Button,
  Skeleton,
  Thumbnail,
  LinkBreadcrumb,
  type BreadcrumbItem,
} from '@dacr/library'
import { TextIcon, DacrMarkIcon, DacrAIIcon } from '@dacr/icons'
import { useDevice } from '@/shared/composables/useDevice'
import axios from 'axios'
import { computed, ref, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ProtectionBadge from '@/shared/components/ProtectionBadge.vue'

const route = useRoute()
const router = useRouter()

const { isMobile } = useDevice()

const fileId = computed(() => route.params.id as string)
const trackIndex = computed(() =>
  route.params.trackIndex !== undefined ? Number(route.params.trackIndex) : null
)

const { file, loading } = useCopyrightFile(fileId)

const previewSrc = computed(() =>
  file.value?.asset?.previewImageUrl
    ? applyAuthorizationToken(file.value.asset.previewImageUrl)
    : undefined
)

const transcriptUrl = computed(() => {
  const analysis = file.value?.analysis
  if (!analysis) return null

  if (
    trackIndex.value !== null &&
    (analysis.__typename === 'VideoAnalysis' ||
      analysis.__typename === 'AudioAnalysis')
  ) {
    return analysis.tracks[trackIndex.value]?.transcript?.url ?? null
  }

  if (
    analysis.__typename === 'AudioAnalysis' ||
    analysis.__typename === 'VideoAnalysis'
  ) {
    return analysis.transcript.url
  }

  return null
})

const transcript = ref<{ timestamp: string; text: string }[]>([])
const transcriptLoading = ref(true)

watchEffect(async (onCleanup) => {
  const url = transcriptUrl.value
  if (!url) {
    transcript.value = []
    return
  }

  transcriptLoading.value = true

  let cancelled = false
  onCleanup(() => {
    cancelled = true
  })

  try {
    const { data } = await axios.get(url)
    if (cancelled) return
    transcript.value = data.lines
  } catch {
    transcript.value = []
  } finally {
    if (!cancelled) transcriptLoading.value = false
  }
})

const fileRoute = computed(() => `/file/${route.params.id}`)

const breadcrumbItems: BreadcrumbItem[] = [
  { label: 'Copyright' },
  { label: 'Transcript' },
]

const handleBreadcrumbSelect = (_item: BreadcrumbItem, index: number) => {
  if (index === 0) router.push(fileRoute.value)
}
</script>
